import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { AuthCard } from '../components/AuthCard';
import { Alert } from '../components/Alert';
import { Button } from '../components/Button';

function isAllowedRedirect(url: string): boolean {
  try {
    const parsed = new URL(url);
    return (
      ['localhost', '127.0.0.1'].includes(parsed.hostname) ||
      parsed.hostname.endsWith('.layerlint.ai') ||
      parsed.hostname.endsWith('.layerlint.io') ||
      parsed.hostname === 'layerlint.ai' ||
      parsed.hostname === 'layerlint.io'
    );
  } catch {
    return false;
  }
}

type CallbackState =
  | { status: 'loading' }
  | { status: 'redirecting'; url: string }
  | { status: 'success'; email: string }
  | { status: 'error'; message: string };

export function Callback() {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<CallbackState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    async function handleCallback() {
      try {
        // Get the session (handles hash fragment tokens from OAuth/magic link)
        const { data, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          if (!cancelled) {
            setState({ status: 'error', message: sessionError.message });
          }
          return;
        }

        const session = data.session;
        if (!session) {
          // Listen for auth state change (e.g., from hash fragment processing)
          const { data: listener } = supabase.auth.onAuthStateChange((_event, newSession) => {
            if (cancelled) return;
            if (newSession) {
              handleSession(newSession.user.email ?? 'Unknown', newSession.access_token);
              listener.subscription.unsubscribe();
            }
          });

          // If no session after a brief wait, show error
          setTimeout(() => {
            if (!cancelled) {
              setState({
                status: 'error',
                message: 'Unable to complete sign in. Please try again.',
              });
              listener.subscription.unsubscribe();
            }
          }, 5000);
          return;
        }

        if (!cancelled) {
          handleSession(session.user.email ?? 'Unknown', session.access_token);
        }
      } catch (err) {
        if (!cancelled) {
          setState({
            status: 'error',
            message: err instanceof Error ? err.message : 'An unexpected error occurred',
          });
        }
      }
    }

    function handleSession(email: string, accessToken: string) {
      if (cancelled) return;

      const redirectTo = searchParams.get('redirect_to');

      // Check for redirect_to query param
      if (redirectTo && isAllowedRedirect(redirectTo)) {
        const redirectUrl = new URL(redirectTo);
        redirectUrl.hash = `access_token=${accessToken}`;
        setState({ status: 'redirecting', url: redirectUrl.toString() });
        window.location.href = redirectUrl.toString();
        return;
      }

      // Check for pending plan in localStorage
      const pendingPlan = localStorage.getItem('layerlint_pending_plan');
      if (pendingPlan && (pendingPlan === 'pro' || pendingPlan === 'team')) {
        localStorage.removeItem('layerlint_pending_plan');
        setState({
          status: 'redirecting',
          url: `/pricing?checkout=${pendingPlan}`,
        });
        window.location.href = `/pricing?checkout=${pendingPlan}`;
        return;
      }

      setState({ status: 'success', email });
    }

    void handleCallback();

    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  if (state.status === 'loading' || state.status === 'redirecting') {
    return (
      <AuthCard title="Completing sign in...">
        <div className="flex flex-col items-center py-8 gap-4">
          <svg
            className="animate-spin h-8 w-8 text-ll-cyan"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <p className="text-sm text-ll-muted">
            {state.status === 'redirecting' ? 'Redirecting...' : 'Completing sign in...'}
          </p>
        </div>
      </AuthCard>
    );
  }

  if (state.status === 'error') {
    return (
      <AuthCard title="Sign in failed">
        <Alert variant="error" message={state.message} />
        <Button
          variant="secondary"
          onClick={() => {
            window.location.href = '/sign-in';
          }}
        >
          Back to sign in
        </Button>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="You're signed in!">
      <div className="text-center py-4">
        <div className="w-12 h-12 bg-ll-success/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-ll-success"
          >
            <polyline points="5 12 10 17 19 7" />
          </svg>
        </div>
        <p className="text-sm text-ll-muted mb-6">
          Signed in as <span className="text-ll-text font-mono">{state.email}</span>
        </p>
        <Button
          variant="secondary"
          onClick={() => {
            window.location.href = '#';
          }}
        >
          Go to dashboard
        </Button>
      </div>
    </AuthCard>
  );
}
